import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, capitalize } from '@mui/material';

import * as S from 'commons/components/Table/Table.styles';
import { BinNetsuiteSyncStatusEnum } from 'commons/enums';
import { IBin } from 'commons/types';
import { RowTitleProps } from 'commons/types/listPageData.interface';
import { UserRolesEnum } from 'commons/types/user.roles.enum';
import { TableActionRow, DataTable } from 'pages/dashboard/commons/components';
import { retryCreateBinAction } from 'redux-stores/actions';
import { selectUser } from 'redux-stores/features/authSlice';
import { AppDispatch } from 'redux-stores/store';

import BinListRowActionBtn from './BinListRowActionBtn.component';

const rowTitles: RowTitleProps[] = [
  { title: 'Bin', sortField: 'name' },
  { title: 'Type' },
  { title: 'Commodity' },
  { title: 'Synced to NetSuite' },
  { title: 'Sync Error Message' },
];

const Row: React.FC<{
  data: IBin;
  location?: number;
  onClickEditBinType: (bin: IBin) => void;
  onClickEditCommodity: (bin: IBin) => void;
  index: number;
}> = ({ data, location, onClickEditBinType, onClickEditCommodity, index }) => {
  const user = useSelector(selectUser);
  const dispatch = useDispatch<AppDispatch>();
  return (
    <S.TableRow>
      <S.TableColumn
        style={{ width: '18%' }}
        data-testid={`listBinListBinNumber${index + 1}`}
      >
        {data.name}
      </S.TableColumn>
      <S.TableColumn
        style={{ width: '12%', textTransform: 'capitalize' }}
        data-testid={`listBinListType${index + 1}`}
      >
        {data.bin_type?.replace('_', ' ') || '-'}
      </S.TableColumn>
      <S.TableColumn
        style={{ width: '18%' }}
        data-testid={`listBinListCommodity${index + 1}`}
      >
        {data.commodity || '-'}
      </S.TableColumn>
      <S.TableColumn
        style={{ width: '16%' }}
        data-testid={`listBinListStatus${index + 1}`}
      >
        {data.netsuite_sync_status === BinNetsuiteSyncStatusEnum.COMPLETED
          ? 'Yes'
          : data.netsuite_sync_status ===
            BinNetsuiteSyncStatusEnum.UPDATE_FAILED
          ? 'No'
          : data.netsuite_sync_status === BinNetsuiteSyncStatusEnum.UPDATING
          ? 'Syncing'
          : ''}
      </S.TableColumn>
      <S.TableColumn
        style={{ width: '20%' }}
        data-testid={`listBinListError${index + 1}`}
      >
        {data.netsuite_error_reason || '-'}
      </S.TableColumn>

      <S.TableColumn
        style={{ width: '16%', textAlign: 'right', paddingLeft: '12px' }}
        data-testid={`listBinListButtons${index + 1}`}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          {data.netsuite_sync_status ===
            BinNetsuiteSyncStatusEnum.UPDATE_FAILED && (
            <Button
              data-testid={`buttonBinListRetry${index + 1}`}
              onClick={() =>
                dispatch(retryCreateBinAction({ bin: data.name, location }))
              }
              variant="outlined"
            >
              Retry
            </Button>
          )}
          {user.roles.includes(UserRolesEnum.SUPER_ADMIN) && (
            <BinListRowActionBtn
              bin={data}
              onClickEditBinType={onClickEditBinType}
              onClickEditCommodity={onClickEditCommodity}
            />
          )}
        </Box>
      </S.TableColumn>
    </S.TableRow>
  );
};

const BinListTable: React.FC<{
  data: IBin[];
  location?: number;
  onClickCreate: () => void;
  onClickEditBinType: (bin: IBin) => void;
  onClickEditCommodity: (bin: IBin) => void;
}> = ({
  data = [],
  location,
  onClickCreate,
  onClickEditBinType,
  onClickEditCommodity,
}): React.ReactNode => {
  return (
    <>
      <TableActionRow>
        <Button onClick={onClickCreate} variant="outlined" fullWidth>
          Create New Bin
        </Button>
      </TableActionRow>
      <DataTable rowTitles={rowTitles} hideCheckbox>
        <S.TableBody>
          {data.map((item, index) => (
            <Row
              data={item}
              location={location}
              onClickEditBinType={onClickEditBinType}
              onClickEditCommodity={onClickEditCommodity}
              index={index}
            />
          ))}
        </S.TableBody>
      </DataTable>
      {data.length > 0 && (
        <TableActionRow>
          <Button onClick={onClickCreate} variant="outlined" fullWidth>
            Create New Bin
          </Button>
        </TableActionRow>
      )}
    </>
  );
};

export default BinListTable;
